<template>
  <div
    class="fts-checkbox"
    :class="[
      checkboxClass,
      { 'checkbox--active': mutableValue },
      { 'checkbox--disabled': disabled },
    ]"
  >
    <div
      class="checkbox-toggle"
      :style="{
        'height': size ? `${size}px` : null,
        'width': size ? `${size}px` : null
      }"
      @click="toggle"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="#fff"
        viewBox="0 0 24 24"
        v-if="mutableValue"
      >
        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"></path>
      </svg>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill=""
        viewBox="0 0 24 24"
        v-else-if="indeterminate"
      >
        <rect x="3" y="11" width="18" height="2" fill="#fff" />
      </svg>
      <input
        :id="reference"
        type="checkbox"
        :value="mutableValue"
        :disabled="disabled"
        class="checkbox-input"
      />
    </div>

    <slot name="checkboxLabel" :referenceId="referenceId">
      <label
        class="checkbox-label"
        :for="reference"
        :style="{
          'font-size': fontSize ? `${fontSize}px` : null,
        }"
        v-if="isDefaultSlot"
      >
        <slot :referenceId="referenceId" />
      </label>
    </slot>
  </div>
</template>

<script>
export default {
  props: {
    value: Boolean,
    indeterminate: Boolean,
    checked: Boolean,
    referenceId: String,
    disabled: Boolean,
    size: Number,
    fontSize: Number,
    color: String,
  },
  model: {
    prop: "value",
    event: "updateValue",
  },
  data: () => ({
    defaultReference: null
  }),
  computed: {
    mutableValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("updateValue", val);
        this.$emit('input', val);
      },
    },
    checkboxClass() {
      let classes = [];
      try {
        if (
          this.color &&
          [
            "primary",
            "secondary",
            "success",
            "danger",
            "warning",
            "dark",
            "ternary-green",
            "ternary-blue",
            "default"
          ].some(x => x === this.color.toLowerCase())
        ) {
          classes.push(`fts-checkbox-${this.color.toLowerCase()}`);
        }
      } catch(err) {}
      return classes.join(" ");
    },
    reference() {
      return this.referenceId || this.defaultReference;
    },
    isDefaultSlot() {
      return Boolean(this.$slots.default) || Boolean(this.$scopedSlots.default);
    }
  },
  methods: {
    toggle() {
      if (this.disabled) return;

      this.mutableValue = !Boolean(this.mutableValue);
    }
  },
  mounted() {
    if (this.checked) {
      this.mutableValue = true;
    }
    this.defaultReference = `inistate-ref-${this._uid}`;
  }
};
</script>

<style lang="scss" scoped>
@use "../../assets/scss/vars.scss" as *;

@mixin initial() {
  $checkboxes: (
    "primary": fts-primary,
    "secondary": fts-secondary,
    "success": fts-success,
    "danger": fts-danger,
    "warning": fts-warning,
    "dark": fts-dark,
    "ternary-green": fts-ternary-green,
    "ternary-blue": fts-ternary-blue,
    "default": fts-default,
  );
  @each $name, $color in $checkboxes {
    &.fts-checkbox-#{$name} {
      &.checkbox--active {
        .checkbox-toggle {
          border-color: var(--#{$color});
          background-color: var(--#{$color});
        }
      }
      &.checkbox-disabled {
        &.checkbox--active {
          .checkbox-toggle {
            @include theme {
              background-color: rgba(theme-get($color), 0.25);
              border-color: rgba(theme-get($color), 0.25);
            }
          }
        }
      }
    }
  }
}

.fts-checkbox {
  $size: $font-size * $line-height;
  display: flex;
  .checkbox-toggle {
    position: relative;
    min-height: $size;
    min-width: $size;
    height: $size;
    width: $size;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--fts-border);
    border-radius: 0.125rem;
    cursor: pointer;
  }
  .checkbox-label {
    padding-left: 0.5rem;
    font-size: $font-size;
    line-height: $line-height;
    color: var(--fts-text);
    user-select: none;
    cursor: pointer;
  }
  .checkbox-input {
    position: absolute;
    z-index: -1;
    visibility: hidden;
  }
  &.checkbox--active {
    .checkbox-toggle {
      border-color: var(--fts-primary);
      background-color: var(--fts-primary);
    }
  }
  &.checkbox--disabled {
    .checkbox-toggle {
      border-color: var(--fts-disabled);
      cursor: not-allowed;
    }
    &.checkbox--active {
      .checkbox-toggle {
        background-color: var(--fts-disabled);
      }
    }
    .checkbox-label {
      color: var(--fts-disabled);
      cursor: not-allowed;
    }
  }
  @include initial();
}
</style>